import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, Send, Calendar, Play, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
interface ContactSectionProps {
  content: {
    title: string;
    subtitle: string;
    form: {
      name: string;
      email: string;
      company: string;
      service: string;
      message: string;
      submit: string;
      services: readonly string[];
    };
    contact: {
      email: string;
      phone: string;
      address: string;
    };
    meeting: {
      question: string;
      button: string;
    };
  };
}
const ContactSection = ({
  content
}: ContactSectionProps) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
    honeypot: "" // Hidden field for bot detection
  });
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: language === "sv" ? "Fyll i alla obligatoriska fält" : "Fill in all required fields",
        description: language === "sv" ? "Namn, e-post och meddelande är obligatoriska" : "Name, email and message are required",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabase.functions.invoke('submit-contact', {
        body: formData
      });
      if (error) throw error;

      // Navigate to thank you page
      navigate('/tack');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: language === "sv" ? "Ett fel uppstod" : "An error occurred",
        description: language === "sv" ? 
          "Kunde inte skicka formuläret. Försök igen eller kontakta oss direkt." :
          "Could not send the form. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <section id="contact" className="py-16 relative overflow-hidden">
      {/* Complex depth of field background with multiple light sources */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-200/30"></div>
      
      {/* Light streams and bokeh effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-radial from-white/40 via-white/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute top-1/3 right-1/3 w-64 h-64 bg-gradient-radial from-amber-100/50 via-amber-50/20 to-transparent rounded-full blur-2xl"></div>
      <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-gradient-radial from-blue-50/30 via-white/15 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute top-2/3 right-1/4 w-48 h-48 bg-gradient-radial from-yellow-50/40 via-white/10 to-transparent rounded-full blur-xl"></div>
      
      {/* Diagonal light streams */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent transform rotate-12 scale-150"></div>
      <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-gray-50/20 to-transparent transform -rotate-6 scale-125"></div>
      <div className="container mx-auto px-4 relative z-10">

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="p-8 text-center">
            <div className="space-y-6 flex flex-col justify-center h-full">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-red-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                {language === "sv" ? "Redo att börja?" : "Ready to get started?"}
              </h2>
              <p className="text-lg text-muted-foreground">
                {language === "sv" ? "Vår svarstid är oftast inom 3h mellan 09-17" : "Our response time is usually within 3 hours between 09-17"}
              </p>
              <div>
                <Button 
                  asChild
                  className="bg-gradient-primary hover:opacity-90 border-0 text-lg px-8 py-6 h-auto"
                >
                  <a
                    href="https://volym.typeform.com/to/vvm1IndH?utm_source=xxxxx&utm_campaign=xxxxx&utm_content=xxxxx"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    {language === "sv" ? "Få prisförslag" : "Request Quote"}
                  </a>
                </Button>
              </div>
            </div>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="p-6">
              <CardHeader className="px-0 pt-0">
                <CardTitle className="flex items-center text-xl">
                  <Mail className="mr-2 h-5 w-5" />
                  {language === "sv" ? "Kontaktinformation" : "Contact Information"}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0 space-y-4">
                <div className="flex items-center">
                  <Mail className="mr-3 h-4 w-4 text-muted-foreground" />
                  <a href="mailto:info@volymkommunikation.se" className="hover:text-primary transition-colors">
                    info@volymkommunikation.se
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="mr-3 h-4 w-4 text-muted-foreground" />
                  <a href="tel:+46107502866" className="hover:text-primary transition-colors">
                    +46107502866
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6">
              <CardHeader className="px-0 pt-0">
                <CardTitle className="flex items-center text-xl">
                  <Calendar className="mr-2 h-5 w-5" />
                  {content.meeting.question}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <p className="text-muted-foreground mb-4">
                  {language === "sv" ? 
                    "Boka ett 15-minutersmöte för att diskutera ditt projekt och få en kostnadsfri konsultation." :
                    "Book a 15-minute meeting to discuss your project and get a free consultation."
                  }
                </p>
                <Button 
                  asChild 
                  className="w-full mb-4"
                  variant="outline"
                >
                  <a 
                    href="https://outlook.office.com/book/BokaMte1@volymkommunikation.se/?ismsaljsauthenabled" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {language === "sv" ? "Boka möte" : "Book Meeting"}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardContent className="px-0 pb-0">
                <h3 className="font-semibold mb-2">
                  {language === "sv" ? "Snabba svar garanterat" : "Fast Responses Guaranteed"}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {language === "sv" ? 
                    "Vi svarar alltid inom 24 timmar och kan ofta hjälpa er redan samma dag." :
                    "We always respond within 24 hours and can often help you the same day."
                  }
                </p>
                <div className="text-sm space-y-1">
                  <p>✓ {language === "sv" ? "Kostnadsfri konsultation" : "Free consultation"}</p>
                  <p>✓ {language === "sv" ? "Snabb offert" : "Quick quote"}</p>
                  <p>✓ {language === "sv" ? "Professionell rådgivning" : "Professional advice"}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
export default ContactSection;